import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext, ConfirmationModal } from 'commons-ui';
import usePermission from 'commons-ui/src/permission/usePermission';
import { ACTION, MISCELLANEOUS_RESOURCES, PAGE_NAMES } from 'constant';
import { IDENTITY_TYPE, MESSAGES, STEPS } from './constants';
import {
	Candidate,
	ExportFormat,
	IdentifyForm as IdentifyFormValues,
	LoadingKind,
	SearchRequest,
	SearchResult,
	Step,
	UserData
} from './types';
import useStyles from './useStyles';
import useDpdpaService from './utils/useDpdpaService';
import { downloadExport } from './utils/exportFile';
import StepRail from './submodules/StepRail';
import ContextBar from './submodules/ContextBar';
import IdentifyForm from './submodules/IdentifyForm';
import CandidateList from './submodules/CandidateList';
import DataView from './submodules/DataView';
import StatusLine from './submodules/common/StatusLine';

const INITIAL_FORM: IdentifyFormValues = {
	ticketId: '',
	reason: '',
	identityType: IDENTITY_TYPE.UID,
	value: ''
};

/**
 * Decrypted personal data lives only in this component's state. It is never written to context,
 * storage, the URL or the console, and is dropped on unmount, on "Edit" and when another account is picked.
 */
const DpdpaDataAccess: React.FC = () => {
	const { classes } = useStyles();
	const { showAlert } = useContext(AppContext);
	const { hasPermission } = usePermission();
	const { searchUser, fetchUserData, auditExport } = useDpdpaService();

	const [step, setStep] = useState<Step>(STEPS.IDENTIFY);
	const [form, setForm] = useState<IdentifyFormValues>(INITIAL_FORM);
	const [searchContext, setSearchContext] = useState<SearchRequest | null>(null);
	const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
	const [pendingCandidate, setPendingCandidate] = useState<Candidate | null>(null);
	const [userData, setUserData] = useState<UserData | null>(null);
	const [loading, setLoading] = useState<LoadingKind>(null);

	// Bumped whenever the view is reset so late responses from abandoned requests are discarded
	const generationRef = useRef<number>(0);
	const isMountedRef = useRef<boolean>(true);
	// 'Show data' can be clicked twice before the modal unmounts; each fetch writes a view audit row
	const reviewInFlightRef = useRef<boolean>(false);

	useEffect(() => {
		// set on (re)mount too: effects can re-run (fast refresh), and a stale `false` would drop every response
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			generationRef.current += 1;
		};
	}, []);

	const isCurrent = (generation: number): boolean => isMountedRef.current && generation === generationRef.current;

	const permissionArgs = { configPageName: PAGE_NAMES.miscellaneous, configResourceName: MISCELLANEOUS_RESOURCES.dpdpaDataAccess };
	const canReview: boolean = hasPermission({ ...permissionArgs, configActionName: ACTION.fetchDpdpaUserData });
	const canExport: boolean = hasPermission({ ...permissionArgs, configActionName: ACTION.exportDpdpaUserData });

	const resetGeneration = (): number => {
		generationRef.current += 1;
		return generationRef.current;
	};

	const handleSearch = async (values: IdentifyFormValues) => {
		const generation: number = resetGeneration();
		const request: SearchRequest = {
			ticketId: values.ticketId.trim(),
			reason: values.reason.trim(),
			identityType: values.identityType,
			value: values.value.trim()
		};
		setUserData(null);
		setSearchResult(null);
		setLoading('SEARCH');
		const result: SearchResult | null = await searchUser(request);
		if (!isCurrent(generation)) return;
		setLoading(null);
		if (!result) return;
		setSearchContext(request);
		setSearchResult({ ...result, candidates: result.candidates || [] });
		setStep(STEPS.SELECT);
	};

	const handleEdit = () => {
		resetGeneration();
		setUserData(null);
		setSearchResult(null);
		setPendingCandidate(null);
		setLoading(null);
		setStep(STEPS.IDENTIFY);
	};

	const handleBackToAccounts = () => {
		resetGeneration();
		setUserData(null);
		setLoading(null);
		setStep(STEPS.SELECT);
	};

	const handleConfirmReview = async () => {
		const candidate: Candidate | null = pendingCandidate;
		setPendingCandidate(null);
		if (!candidate || !searchContext || reviewInFlightRef.current) return;
		reviewInFlightRef.current = true;
		const generation: number = resetGeneration();
		setUserData(null);
		setLoading('FETCH');
		const data: UserData | null = await fetchUserData({
			uid: candidate.uid,
			ticketId: searchContext.ticketId,
			reason: searchContext.reason
		});
		reviewInFlightRef.current = false;
		if (!isCurrent(generation)) return;
		setLoading(null);
		if (!data) return;
		setUserData({ ...data, sources: data.sources || [], warnings: data.warnings || [] });
		setStep(STEPS.REVIEW);
	};

	const handleExport = async (format: ExportFormat) => {
		if (!userData || !searchContext) return;
		const generation: number = generationRef.current;
		setLoading('EXPORT');
		const audit = await auditExport({
			uid: userData.uid,
			ticketId: searchContext.ticketId,
			reason: searchContext.reason,
			format
		});
		if (!isCurrent(generation)) return;
		setLoading(null);
		// The file is only built once the export has been audited
		if (!audit) return;
		try {
			downloadExport(userData, searchContext.ticketId, format);
		} catch (err) {
			showAlert(MESSAGES.EXPORT_FAILED, { type: 'error' });
		}
	};

	return (
		<div className={classes.scrollArea}>
			<div className={classes.page}>
				<div>
					<h2 className={classes.title}>DPDPA Data Access</h2>
					<p className={classes.subtitle}>
						Retrieve the personal data held for a data principal under the DPDP Act. Searches, views and exports are
						logged for audit.
					</p>
				</div>
				<StepRail step={step} />
				{step !== STEPS.IDENTIFY && searchContext && (
					<ContextBar context={searchContext} onEdit={handleEdit} isEditDisabled={loading === 'EXPORT'} />
				)}
				<StatusLine loading={loading} />

				{step === STEPS.IDENTIFY && (
					<IdentifyForm values={form} onChange={setForm} onSubmit={handleSearch} isLoading={!!loading} />
				)}

				{step === STEPS.SELECT && searchResult && searchContext && (
					<CandidateList
						result={searchResult}
						identityType={searchContext.identityType}
						canReview={canReview}
						isLoading={!!loading}
						onReview={setPendingCandidate}
					/>
				)}

				{step === STEPS.REVIEW && userData && (
					<DataView
						data={userData}
						canExport={canExport}
						isLoading={!!loading}
						onExport={handleExport}
						onBack={handleBackToAccounts}
					/>
				)}

				{pendingCandidate && searchContext && (
					<ConfirmationModal
						confirmationHeader={MESSAGES.CONFIRM_HEADER}
						confirmationMessage={`Ticket ${searchContext.ticketId} · UID ${pendingCandidate.uid} · Reason: ${searchContext.reason}. Decrypted personal data will be shown and this access is logged for audit.`}
						agreeLabel={MESSAGES.CONFIRM_AGREE}
						closeLabel={MESSAGES.CONFIRM_CLOSE}
						onClose={() => setPendingCandidate(null)}
						confirmationDialogOnAgree={handleConfirmReview}
					/>
				)}
			</div>
		</div>
	);
};

export default DpdpaDataAccess;
